import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './PrivateLessons.css';
import { API_BASE, authHeaders } from './privateLessonUtils.js';

function PrivateLessonTeacherLinks() {
  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState('');

  const emptyForm = {
    student_id: '',
    docent_id: '',
    subject: '',
    is_active: true
  };

  const [form, setForm] = useState(emptyForm);

  const normalizeArray = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.Data)) return payload.Data;
    return [];
  };

  const getStudentId = (student) => student.student_id || student.id || '';
  const getTeacherId = (teacher) => teacher.docent_id || teacher.id || '';

  const getStudentName = (student) => {
    return (
      student.student_name ||
      `${student.firstname || ''} ${student.lastname || ''}`.trim() ||
      student.email ||
      getStudentId(student)
    );
  };

  const getTeacherName = (teacher) => {
    return (
      teacher.docent_name ||
      `${teacher.firstname || ''} ${teacher.lastname || ''}`.trim() ||
      teacher.email ||
      getTeacherId(teacher)
    );
  };

  const normalizeLocationIds = (teacher) => {
    if (Array.isArray(teacher.location_ids)) return teacher.location_ids.filter(Boolean);

    if (Array.isArray(teacher.locations)) {
      return teacher.locations
        .map((location) => location.location_id || location.id || location)
        .filter(Boolean);
    }

    if (teacher.location_id) return [teacher.location_id];

    return [];
  };

  const fetchMeta = async () => {
    try {
      const res = await axios.get(`${API_BASE}/private-lesson-teacher-link/meta`, {
        headers: authHeaders()
      });

      setStudents(normalizeArray(res.data?.data?.students));
      setTeachers(normalizeArray(res.data?.data?.teachers));
    } catch (err) {
      console.error('Error fetching private lesson teacher-link metadata', err);
      setStudents([]);
      setTeachers([]);
    }
  };

  const fetchLinks = async () => {
    try {
      setIsLoading(true);

      const res = await axios.get(`${API_BASE}/private-lesson-teacher-link/getall`, {
        params: {
          search,
          showInactive
        },
        headers: authHeaders()
      });

      setItems(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error('Error fetching private lesson teacher links', err);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta();
    fetchLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const selectedStudent = useMemo(() => {
    return students.find((item) => getStudentId(item) === form.student_id) || null;
  }, [students, form.student_id]);

  const selectedStudentLocationId = selectedStudent?.location_id || null;

  const filteredTeachers = useMemo(() => {
    if (!selectedStudent) return [];

    if (!selectedStudentLocationId) {
      return teachers;
    }

    return teachers.filter((teacher) => {
      const locationIds = normalizeLocationIds(teacher);

      // Single-user klanten of oudere endpoints kunnen geen location_ids teruggeven.
      // In dat geval laten we de docent staan en laat de backend definitief valideren.
      if (locationIds.length === 0) return true;

      return locationIds.includes(selectedStudentLocationId);
    });
  }, [teachers, selectedStudent, selectedStudentLocationId]);

  const selectedTeacherIsAllowed = useMemo(() => {
    if (!form.docent_id) return true;
    return filteredTeachers.some((teacher) => getTeacherId(teacher) === form.docent_id);
  }, [filteredTeachers, form.docent_id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      if (name === 'student_id') {
        const student = students.find((item) => getStudentId(item) === value) || null;
        const nextLocationId = student?.location_id || null;

        if (prev.docent_id) {
          const currentTeacher = teachers.find((teacher) => getTeacherId(teacher) === prev.docent_id);
          const teacherLocationIds = currentTeacher ? normalizeLocationIds(currentTeacher) : [];

          if (
            nextLocationId &&
            teacherLocationIds.length > 0 &&
            !teacherLocationIds.includes(nextLocationId)
          ) {
            next.docent_id = '';
          }
        }
      }

      return next;
    });
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId('');
  };

  const startEdit = (item) => {
    setEditingId(item.link_id);
    setForm({
      student_id: item.student_id || '',
      docent_id: item.docent_id || '',
      subject: item.subject || '',
      is_active: item.is_active !== false
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.student_id) {
      alert('Selecteer een leerling.');
      return;
    }

    if (!form.docent_id) {
      alert('Selecteer een docent.');
      return;
    }

    if (!selectedTeacherIsAllowed) {
      alert('Deze docent is niet gekoppeld aan de locatie van de gekozen leerling.');
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        student_id: form.student_id,
        docent_id: form.docent_id,
        subject: form.subject?.trim() || null,
        is_active: form.is_active
      };

      if (editingId) {
        await axios.put(`${API_BASE}/private-lesson-teacher-link/update/${editingId}`, payload, {
          headers: authHeaders()
        });
      } else {
        await axios.post(`${API_BASE}/private-lesson-teacher-link/add`, payload, {
          headers: authHeaders()
        });
      }

      if (!form.is_active) {
        setShowInactive(true);
      }

      resetForm();
      fetchLinks();
    } catch (err) {
      console.error('Error saving private lesson teacher link', err);
      alert(err?.response?.data?.error || 'Opslaan mislukt.');
    } finally {
      setIsSaving(false);
    }
  };

  const removeLink = async (id) => {
    const confirmed = window.confirm('Weet je zeker dat je deze koppeling wilt verwijderen?');
    if (!confirmed) return;

    try {
      await axios.delete(`${API_BASE}/private-lesson-teacher-link/delete/${id}`, {
        headers: authHeaders()
      });
      fetchLinks();
    } catch (err) {
      console.error('Error deleting private lesson teacher link', err);
      alert(err?.response?.data?.error || 'Verwijderen mislukt.');
    }
  };

  const toggleActive = async (item) => {
    try {
      const payload = {
        student_id: item.student_id,
        docent_id: item.docent_id,
        subject: item.subject || null,
        is_active: !item.is_active
      };

      await axios.put(`${API_BASE}/private-lesson-teacher-link/update/${item.link_id}`, payload, {
        headers: authHeaders()
      });

      if (item.is_active) {
        setShowInactive(true);
      }

      fetchLinks();
    } catch (err) {
      console.error('Error toggling private lesson teacher link', err);
      alert(err?.response?.data?.error || 'Status wijzigen mislukt.');
    }
  };

  return (
    <div className="bg-white p-4">
      <h2 className="text-center">Privelesdocenten koppelen</h2>
      <hr className="seperator" />

      <form onSubmit={handleSubmit} className="form-student mb-4">
        <h4>{editingId ? 'Koppeling bewerken' : 'Nieuwe koppeling'}</h4>

        <div className="alert alert-light border">
          Koppel een docent aan een specifieke leerling en eventueel aan een vak.
          De locatie wordt automatisch bepaald op basis van de gekozen leerling.
        </div>

        <div className="row">
          <div className="mb-3 col-md-6">
            <label>Leerling *</label>
            <select
              className="form-select rounded-0"
              name="student_id"
              value={form.student_id}
              onChange={handleChange}
              required
            >
              <option value="">Selecteer leerling</option>
              {students.map((student) => (
                <option key={getStudentId(student)} value={getStudentId(student)}>
                  {getStudentName(student)}
                  {student.location_name ? ` — ${student.location_name}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-3 col-md-6">
            <label>Locatie leerling</label>
            <input
              className="form-control rounded-0"
              value={selectedStudent?.location_name || selectedStudent?.location_id || '-'}
              readOnly
            />
          </div>
        </div>

        <div className="row">
          <div className="mb-3 col-md-6">
            <label>Docent *</label>
            <select
              className="form-select rounded-0"
              name="docent_id"
              value={form.docent_id}
              onChange={handleChange}
              required
              disabled={!selectedStudent}
            >
              <option value="">
                {selectedStudent ? 'Selecteer docent' : 'Selecteer eerst een leerling'}
              </option>
              {filteredTeachers.map((teacher) => (
                <option key={getTeacherId(teacher)} value={getTeacherId(teacher)}>
                  {getTeacherName(teacher)}
                </option>
              ))}
            </select>
            {selectedStudent && filteredTeachers.length === 0 && (
              <div className="form-text text-danger">
                Geen docenten gevonden voor de locatie van deze leerling.
              </div>
            )}
          </div>

          <div className="mb-3 col-md-6">
            <label>Vak / onderwerp</label>
            <input
              type="text"
              className="form-control rounded-0"
              name="subject"
              value={form.subject}
              onChange={handleChange}
              placeholder="Bijvoorbeeld Rekenen"
            />
          </div>
        </div>

        <div className="form-check mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="is_active"
            name="is_active"
            checked={form.is_active}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="is_active">
            Actief
          </label>
        </div>

        <div className="row">
          <div className="col-md-6 mb-2">
            <button type="submit" className="btn btn-secondary w-100 rounded-0" disabled={isSaving}>
              {isSaving ? 'Opslaan...' : editingId ? 'Wijziging opslaan' : 'Koppeling toevoegen'}
            </button>
          </div>
          <div className="col-md-6 mb-2">
            <button type="button" className="btn btn-outline-secondary w-100 rounded-0" onClick={resetForm} disabled={isSaving}>
              Leegmaken
            </button>
          </div>
        </div>
      </form>

      <div className="row mb-3">
        <div className="col-md-7 mb-2">
          <input
            type="text"
            className="form-control rounded-0"
            placeholder="Zoek op leerling, docent of vak"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') fetchLinks();
            }}
          />
        </div>
        <div className="col-md-3 mb-2 d-flex align-items-center">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="showInactive"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="showInactive">
              Toon inactieve koppelingen
            </label>
          </div>
        </div>
        <div className="col-md-2 mb-2">
          <button type="button" className="btn btn-outline-secondary w-100 rounded-0" onClick={fetchLinks}>Zoeken</button>
        </div>
      </div>

      {isLoading ? (
        <p>Loading...</p>
      ) : items.length === 0 ? (
        <div className="alert alert-light border">Geen koppelingen gevonden.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered align-middle">
            <thead>
              <tr>
                <th>Leerling</th>
                <th>Locatie</th>
                <th>Docent</th>
                <th>Vak</th>
                <th>Status</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.link_id} className={item.is_active ? '' : 'table-light text-muted'}>
                  <td>{item.student_name || '-'}</td>
                  <td>{item.location_name || '-'}</td>
                  <td>{item.docent_name || '-'}</td>
                  <td>{item.subject || '-'}</td>
                  <td>
                    <span className={`badge ${item.is_active ? 'bg-success' : 'bg-secondary'}`}>
                      {item.is_active ? 'Actief' : 'Inactief'}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex gap-2 flex-wrap">
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => startEdit(item)}>
                        Bewerken
                      </button>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => toggleActive(item)}>
                        {item.is_active ? 'Inactief maken' : 'Actief maken'}
                      </button>
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeLink(item.link_id)}>
                        Verwijderen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PrivateLessonTeacherLinks;
